import React, { useState, useEffect } from "react";
import {
  FileText, Tag, CheckCircle, XCircle,
  Pencil, Trash2, Hash, Mail, Eye, FlaskConical, ShieldCheck, ShieldAlert,
  Copy, Clock,
} from "lucide-react";
import Skeleton from "components/ui/Skeleton";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { getRelativeTime } from "services/date-converter";
import apiEmailTemplate from "../api";
import apiEmailManagement from "../../api";
import EmailTemplatePreview from "../EmailTemplatePreview";
import TestTemplateDialog from "../TestTemplateDialog";
import TemplateWizardModal from "../template-wizard/TemplateWizardModal";

const EMAIL_TYPE_FILTERS = ["ALL", "AUTH", "PROJECT_NOTIFICATION", "HELP", "CHAT_MESSAGE", "RESET_PASSWORD_OTP", "OTHER"];

const EMAIL_TYPE_STYLE = {
  AUTH:                 { bg: "bg-info_bg",        text: "text-info",      border: "border-info_border" },
  PROJECT_NOTIFICATION: { bg: "bg-primary_tint",   text: "text-primary",   border: "border-primary/20" },
  HELP:                 { bg: "bg-warning_bg",      text: "text-warning",   border: "border-warning_border" },
  CHAT_MESSAGE:         { bg: "bg-success_bg",      text: "text-success",   border: "border-success_border" },
  RESET_PASSWORD_OTP:   { bg: "bg-high_bg",         text: "text-high",      border: "border-high_border" },
  OTHER:                { bg: "bg-surface_alt",     text: "text-text_secondary", border: "border-border" },
};

const stripHtml = (html = "") => html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// Scoped list of email templates for a single mailing category (NOTIFICATIONS | MARKETING).
// The owning tab fixes `mailingFor`, so the list always filters by it and new templates
// created here default to that same category.
const TemplateList = ({ mailingFor = "NOTIFICATIONS", onTotalCountChange }) => {
  const { showAlert } = useAlert();

  const [records,       setRecords]       = useState([]);
  const [totalCount,    setTotalCount]    = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [typeFilter,    setTypeFilter]    = useState("ALL");
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [resetTrigger,  setResetTrigger]  = useState(0);
  const [selectedRows,  setSelectedRows]  = useState([]);
  const [modalId,       setModalId]       = useState(null);
  const [isModalOpen,   setIsModalOpen]   = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [testTemplateRow, setTestTemplateRow] = useState(null);
  const [confirmData,   setConfirmData]   = useState({
    isOpen: false, title: "", message: "", confirmText: "Yes", cancelText: "Cancel", icon: null, resolve: null,
  });

  // ── Data Fetching ──────────────────────────────────────────────────────────

  const load = async (page, size, q, emailType = typeFilter) => {
    setLoading(true);
    try {
      const res = await apiEmailManagement.getAllEmailTemplates({
        searchText:  q.trim(),
        limit:       size,
        skip:        (page - 1) * size,
        mailing_for: mailingFor,
        ...(emailType !== "ALL" && { email_type: emailType }),
      });
      setRecords(res.data?.data || []);
      setTotalCount(res.data?.total_count || 0);
      onTotalCountChange?.(res.data?.total_count || 0);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to load templates." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearch("");
    setTypeFilter("ALL");
    setCurrentPage(1);
    setSelectedRows([]);
    setResetTrigger((k) => k + 1);
    load(1, pageSize, "", "ALL");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailingFor]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = (val) => {
    setSearch(val);
    setCurrentPage(1);
    load(1, pageSize, val, typeFilter);
  };

  const handleRefresh = () => {
    setSearch("");
    setTypeFilter("ALL");
    setCurrentPage(1);
    setResetTrigger((k) => k + 1);
    load(1, pageSize, "", "ALL");
  };

  const handleTypeFilter = (val) => {
    setTypeFilter(val);
    setCurrentPage(1);
    load(1, pageSize, search, val);
  };

  const handlePageChange = (page, newSize = pageSize) => {
    const s = newSize;
    const p = newSize !== pageSize ? 1 : page;
    setCurrentPage(p);
    setPageSize(s);
    load(p, s, search, typeFilter);
  };

  const handleCreate     = () => { setModalId(null); setDuplicateSource(null); setIsModalOpen(true); };
  const handleEdit       = (id) => { setModalId(id); setDuplicateSource(null); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setModalId(null); setDuplicateSource(null); };

  const handleDuplicate = (row) => {
    setModalId(null);
    setDuplicateSource({
      template_name: `${row.template_name} (Copy)`,
      template_key:  "",
      email_type:    row.email_type,
      subject:       row.subject,
      eyebrow:       row.eyebrow,
      heading:       row.heading,
      description:   row.description,
      body_html:     row.body_html,
      is_activated:  false,
    });
    setIsModalOpen(true);
  };

  const openConfirm = (opts) =>
    new Promise((resolve) => setConfirmData({ ...opts, isOpen: true, resolve }));

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((p) => ({ ...p, isOpen: false, resolve: null }));
  };

  const handleDelete = async (row) => {
    const confirmed = await openConfirm({
      title:       "Delete Template",
      message:     `Are you sure you want to delete "${row.template_name}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText:  "Cancel",
      variant:     "danger",
      icon:        <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiEmailTemplate.delete(row.id);
      if (res.data?.status) {
        showAlert({ type: "success", message: "Email template deleted successfully." });
        load(currentPage, pageSize, search);
      } else {
        showAlert({ type: "warning", message: res.data?.message });
      }
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Delete failed." });
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return;
    const confirmed = await openConfirm({
      title:       "Delete Selected",
      message:     `Delete ${selectedRows.length} template(s)? This cannot be undone.`,
      confirmText: "Delete All",
      cancelText:  "Cancel",
      variant:     "danger",
      icon:        <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      await Promise.all(selectedRows.map((id) => apiEmailTemplate.delete(id)));
      showAlert({ type: "success", message: `${selectedRows.length} template(s) deleted successfully.` });
      setSelectedRows([]);
      load(1, pageSize, search);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Bulk delete failed." });
    }
  };

  const handleToggleSelect = (id) =>
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  const handleOpenTestTemplate = (row) => setTestTemplateRow(row);
  const handleCloseTestTemplate = () => setTestTemplateRow(null);

  const handleSendTestTemplate = async ({ data_points, test_email }) => {
    try {
      const res = await apiEmailTemplate.testTemplate(testTemplateRow.id, { data_points, test_email });
      if (res.data?.status) {
        showAlert({ type: "success", message: res.data.message });
        handleCloseTestTemplate();
      } else {
        showAlert({ type: "warning", message: res.data?.message || "Test failed." });
      }
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to send test email." });
    }
  };

  // ── Card Helpers ──────────────────────────────────────────────────────────

  const renderStatusBadge = (row) =>
    row.is_activated ? (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-success_bg text-success rounded-lg text-[10.5px] font-medium border border-success_border">
        <CheckCircle size={11} /> Active
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface_alt text-text_secondary rounded-lg text-[10.5px] font-medium border border-border">
        <XCircle size={11} /> Inactive
      </span>
    );

  const renderVerifiedBadge = (row) =>
    row.is_verified ? (
      <ShieldCheck size={14} className="text-success flex-shrink-0" />
    ) : (
      <ShieldAlert size={14} className="text-warning flex-shrink-0" />
    );

  const ACTION_BTN = "inline-flex h-8 w-8 items-center justify-center rounded-lg text-text_muted";

  const renderCardActions = (row) => (
    <div className="flex items-center gap-0.5 shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); setPreviewTemplate(row); }}
        className={`${ACTION_BTN} hover:bg-surface_alt hover:text-text_primary`}
        title="Preview Template"
      >
        <Eye size={15} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleOpenTestTemplate(row); }}
        className={`${ACTION_BTN} hover:bg-success_bg hover:text-success`}
        title="Test Template"
      >
        <FlaskConical size={15} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
        className={`${ACTION_BTN} hover:bg-primary_tint hover:text-primary`}
        title="Edit Template"
      >
        <Pencil size={15} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleDuplicate(row); }}
        className={`${ACTION_BTN} hover:bg-surface_alt hover:text-text_primary`}
        title="Duplicate Template"
      >
        <Copy size={15} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
        className={`${ACTION_BTN} hover:bg-danger_bg hover:text-danger`}
        title="Delete Template"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const activeCount = records.filter((r) => r.is_activated).length;

  return (
    <div className="h-full overflow-y-auto">
    <div className="space-y-4 pt-6 pb-4">

      {/* Summary Stats */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1.5 bg-primary_tint text-primary rounded-lg text-xs font-medium border border-primary/20">
          Total: {totalCount}
        </span>
        <span className="px-3 py-1.5 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
          Active: {activeCount}
        </span>
      </div>

      {/* Toolbar */}
      <TableToolBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onCreate={handleCreate}
        onDeleteSelected={handleBulkDelete}
        selectedCount={selectedRows.length}
        resetSearchTrigger={resetTrigger}
        canCreate
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text_muted whitespace-nowrap">Type:</span>
          <select
            value={typeFilter}
            onChange={(e) => handleTypeFilter(e.target.value)}
            className="h-7 cursor-pointer rounded-md border border-border bg-surface px-2 text-[12px] font-medium text-text_primary transition-colors hover:border-border_strong focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
          >
            {EMAIL_TYPE_FILTERS.map((t) => (
              <option key={t} value={t} className="bg-surface text-text_primary">
                {t === "ALL" ? "All Types" : t.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Card Grid / Empty / Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {[...Array(Math.min(pageSize, 9))].map((_, i) => (
            <div key={i} className="rounded-2xl border border-border bg-surface overflow-hidden">
              <div className="flex items-start justify-between gap-3 px-5 pt-5">
                <div className="flex items-start gap-3.5 flex-1">
                  <Skeleton className="h-12 w-12 flex-shrink-0" rounded="rounded-xl" />
                  <div className="flex-1 space-y-2 pt-0.5">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-4 w-28" rounded="rounded-md" />
                  </div>
                </div>
                <Skeleton className="h-5 w-14" rounded="rounded-lg" />
              </div>
              <div className="px-5 pt-3.5 pb-4 space-y-3">
                <Skeleton className="h-7 w-full" />
                <Skeleton className="h-20 w-full" rounded="rounded-xl" />
              </div>
              <div className="flex items-center justify-between border-t border-border px-5 py-3.5">
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-14" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <Skeleton className="h-8 w-36" rounded="rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : records.length === 0 ? (
        <NoDataState
          icon="file"
          title="No Email Templates"
          message={
            search || typeFilter !== "ALL"
              ? "No templates match your filters. Try adjusting them."
              : "Create your first email template to start sending branded emails."
          }
          showRefresh={false}
          onRefresh={handleRefresh}
          showCreate={true}
          createText="Create New Email Template"
          onCreate={handleCreate}
          size="medium"
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {records.map((row) => {
              const s = EMAIL_TYPE_STYLE[row.email_type] || EMAIL_TYPE_STYLE.OTHER;
              const isSelected = selectedRows.includes(row.id);
              const lastUpdated = row.updated_date || row.created_date;

              return (
                <div
                  key={row.id}
                  className={`flex flex-col overflow-hidden rounded-2xl border bg-surface ${
                    isSelected ? "border-primary/60 bg-primary/[0.03]" : "border-border hover:border-border_strong"
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 px-5 pt-5">
                    <div className="flex items-start gap-3.5 min-w-0">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${s.bg} ${s.border}`}>
                        <FileText size={21} className={s.text} />
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <h3 className="text-[15px] font-semibold text-text_primary truncate" title={row.template_name}>
                            {row.template_name}
                          </h3>
                          {renderVerifiedBadge(row)}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {row.email_type && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium border ${s.bg} ${s.text} ${s.border}`}>
                              <Tag size={9} />
                              {row.email_type.replace(/_/g, " ")}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-surface_alt px-1.5 py-0.5 font-mono text-[10px] font-semibold text-text_muted">
                            <Hash size={9} />
                            {row.template_key || "custom"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {renderStatusBadge(row)}
                      <label
                        className="flex items-center cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(row.id)}
                          className="w-3.5 h-3.5 cursor-pointer accent-primary"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-col gap-3 px-5 pt-3.5 pb-4">
                    <p className="min-h-[2.5em] text-[12.5px] leading-relaxed text-text_secondary line-clamp-2">
                      {row.description || "No description provided for this template."}
                    </p>

                    <div className="rounded-xl border border-border bg-surface_alt px-3.5 py-3">
                      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-text_muted">
                        <Mail size={11} /> Subject
                      </div>
                      <p className="truncate text-[12.5px] font-medium text-text_primary" title={row.subject}>
                        {row.subject || "—"}
                      </p>
                      {row.body_html && (
                        <p className="mt-1 line-clamp-2 text-[11px] text-text_muted">
                          {stripHtml(row.body_html)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="mt-auto flex items-center justify-between gap-2 border-t border-border px-5 py-3.5">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="font-mono text-[10.5px] font-semibold text-text_muted">
                        {`#ET${String(row.id).padStart(3, "0")}`}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-text_muted">
                        <Clock size={10} className="shrink-0" />
                        {lastUpdated ? getRelativeTime(lastUpdated) : "—"}
                      </span>
                    </div>
                    {renderCardActions(row)}
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            selectedCount={selectedRows.length}
          />
        </>
      )}
    </div>

    {/* Overlays live OUTSIDE the space-y-4 flow container: a `space-y-*` utility
        adds margin-top to non-first children, which would shift a fixed inset-0
        overlay (backdrop/blur) down and leave a gap at the top of the screen. */}

    {/* Create / Edit Wizard */}
    <TemplateWizardModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      id={modalId}
      mailingFor={mailingFor}
      duplicateFrom={duplicateSource}
      onSuccess={() => load(currentPage, pageSize, search)}
    />

    {/* Confirm Dialog */}
    {confirmData.isOpen && (
      <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
    )}

    {/* Template Preview */}
    {previewTemplate && (
      <EmailTemplatePreview
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
    )}

    {/* Test Template Dialog */}
    {testTemplateRow && (
      <TestTemplateDialog
        template={testTemplateRow}
        onClose={handleCloseTestTemplate}
        onSend={handleSendTestTemplate}
      />
    )}
    </div>
  );
};

export default TemplateList;
